import { useCallback, useEffect, useState } from 'react';
import { Loader, AlertCircle, MessageSquare, Send, Pencil, Check, X, ImagePlus, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { usernameCacheService } from '../../services/usernameCache';
import type { EventComment, EventMedia } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { EventMediaThumbnail } from './EventMediaThumbnail';
import { CreatorProfile } from '../CreatorProfile';
import { ConfirmationModal } from '../ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface EventCommentsSectionProps {
  targetType: 'event' | 'project';
  targetId: string;
  perms: EventTeamPerms;
  /**
   * Event-only: mirror plain-text comments to linked kanban cards via
   * `POST /events/items/{id}/comments/with-sync` (JSON only — guide §1.5).
   * Image comments always use the legacy multipart route.
   */
  useSync?: boolean;
  refreshNonce?: number;
  onChanged?: () => void;
  /** Open an image in the parent's lightbox. */
  onOpenImage?: (media: EventMedia) => void;
}

/** Comments with image attachments (JSON or multipart on the same path — guide §4). */
export const EventCommentsSection = ({
  targetType,
  targetId,
  perms,
  useSync = false,
  refreshNonce = 0,
  onChanged,
  onOpenImage,
}: EventCommentsSectionProps) => {
  const [comments, setComments] = useState<EventComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newContent, setNewContent] = useState('');
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEditId, setSavingEditId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventComment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.getComments(targetType, targetId);
      const sorted = [...data].sort((a, b) => a.created_at - b.created_at);
      setComments(sorted);
      usernameCacheService.prefetchUsernames(sorted.map((c) => c.author_id).filter(Boolean));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) setPendingImages((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    const content = newContent.trim();
    if (!content && pendingImages.length === 0) return;
    setSubmitting(true);
    try {
      if (pendingImages.length > 0) {
        // Multipart route: `content` field + `files*` image parts (guide §4).
        const formData = new FormData();
        formData.append('content', content);
        pendingImages.forEach((f, i) => formData.append(`files[${i}]`, f));
        await eventTeamApiService.addCommentWithImages(targetType, targetId, formData);
      } else if (targetType === 'event' && useSync) {
        await eventTeamApiService.addCommentWithSync(targetId, content);
      } else {
        await eventTeamApiService.addComment(targetType, targetId, content);
      }
      setNewContent('');
      setPendingImages([]);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async (comment: EventComment) => {
    if (!editDraft.trim()) return;
    setSavingEditId(comment.id);
    try {
      await eventTeamApiService.updateComment(comment.id, editDraft.trim());
      setEditingId(null);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to edit comment');
    } finally {
      setSavingEditId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await eventTeamApiService.deleteComment(deleteTarget.id);
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete comment');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <SkeletonLoader className="h-4 w-32" />
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonLoader key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader title="Comments" icon={MessageSquare} count={comments.length} className="mb-0" />

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {comments.length === 0 && !error ? (
        <EmptyState bare compact icon={MessageSquare} title="No comments" description={perms.canCreate ? 'Start the discussion below.' : undefined} />
      ) : (
        <div className="space-y-2">
          {comments.map((comment) => {
            const isEditing = editingId === comment.id;
            return (
              <div key={comment.id} className="subcard p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <div className="flex items-center gap-2 text-xs text-glass-500 flex-wrap tabular-nums">
                    <CreatorProfile creatorId={comment.author_id} inline />
                    <span>{formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}</span>
                    {comment.updated_at && <span className="italic">(edited)</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    {perms.canCreate && !isEditing && (
                      <button
                        onClick={() => {
                          setEditingId(comment.id);
                          setEditDraft(comment.content);
                        }}
                        className="btn-icon btn-icon-sm"
                        title="Edit comment"
                        aria-label="Edit comment"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {perms.canCreate && (
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(comment)}
                        className="btn-icon btn-icon-sm hover:text-red-300"
                        title="Delete comment (removes attached images too)"
                        aria-label="Delete comment"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      className="glass-input w-full text-sm min-h-[60px]"
                      autoFocus
                    />
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="btn-icon btn-icon-sm"
                        title="Cancel"
                        aria-label="Cancel editing"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleSaveEdit(comment)}
                        disabled={savingEditId === comment.id || !editDraft.trim()}
                        className="btn-icon btn-icon-sm text-green-400"
                        title="Save"
                        aria-label="Save comment"
                      >
                        {savingEditId === comment.id ? (
                          <Loader className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-glass-200 whitespace-pre-wrap">{comment.content}</p>
                )}

                {(comment.images?.length ?? 0) > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {comment.images!.map((img) => (
                      <EventMediaThumbnail
                        key={img.id}
                        media={img}
                        className="w-24 h-24"
                        onClick={onOpenImage ? () => onOpenImage(img) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {perms.canCreate && (
        <div className="subcard p-3 space-y-2">
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="Write a comment… (attach images below)"
            className="glass-input w-full text-sm min-h-[60px]"
          />
          {pendingImages.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {pendingImages.map((file, i) => (
                <span key={`${file.name}-${i}`} className="chip chip-removable gap-1">
                  {file.name}
                  <button
                    type="button"
                    onClick={() => setPendingImages((prev) => prev.filter((_, j) => j !== i))}
                    className="hover:text-red-300"
                    title="Remove attachment"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <label className="glass-button-secondary btn-xs cursor-pointer">
              <ImagePlus className="w-3.5 h-3.5" />
              Attach images
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleFilePick} />
            </label>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting || (!newContent.trim() && pendingImages.length === 0)}
              className="glass-button-sm"
            >
              {submitting ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Comment
            </button>
          </div>
          {useSync && pendingImages.length > 0 && (
            <p className="text-xs text-glass-500">Comments with images don't mirror to kanban.</p>
          )}
        </div>
      )}

      {deleteTarget && (
        <ConfirmationModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => void handleDelete()}
          title="Delete Comment"
          message="Delete this comment? Any images attached to it will be removed too."
          confirmText="Delete"
          isLoading={deleting}
        />
      )}
    </div>
  );
};
